// Source-level statement-boundary scanner for the IntelliSense Engine.
// PRD: Slice "CompletionContext + Statement-Isolation Parser" — given a
// raw SQL buffer and a cursor offset, return the half-open [start, end)
// range of the statement the cursor is in. The orchestrator uses this
// range to keep CompletionContext deterministic across incomplete buffers
// where the tree-sitter grammar's recovery is awkward (e.g. an ERROR node
// after a trailing keyword like `WHERE `).
//
// Splits on top-level `;` for every dialect. Oracle additionally accepts a
// line-leading `/` as a separator (SQL*Plus / SQLcl convention). MSSQL `GO`
// is a batch separator at the SSMS layer, not a parser-level statement
// terminator, and is therefore intentionally NOT treated as a separator
// here.
//
// `;` and `/` inside any string / comment / quoted identifier construct
// are ignored — `sourceScan.ts` owns that classification.

import type { DatabaseEngineId } from '../../../core/engines';

import {
  skipBacktickId,
  skipBlockComment,
  skipBracketedId,
  skipDoubleQuoted,
  skipSingleQuoted,
  skipToEol,
  tryDollarQuoted,
} from './sourceScan';

export type StatementSpan = {
  readonly start: number;
  readonly end: number;
};

/**
 * Returns the half-open `[start, end)` range of the statement the cursor at
 * `offset` belongs to. Both bounds are clamped to `[0, source.length]`.
 *
 * When the cursor sits exactly on a `;` boundary, the next (possibly empty)
 * statement wins — Monaco's column model treats the cursor as "before" the
 * indexed character.
 */
export function findEnclosingStatement(
  source: string,
  offset: number,
  engineId: DatabaseEngineId,
): StatementSpan {
  const length = source.length;
  const clampedOffset = Math.max(0, Math.min(length, offset));
  const separators = scanTopLevelSeparators(source, engineId);

  let prev = -1;
  for (const sep of separators) {
    if (sep < clampedOffset) prev = sep;
    else break;
  }
  let next = length;
  for (const sep of separators) {
    if (sep >= clampedOffset) {
      next = sep;
      break;
    }
  }

  const rawStart = prev + 1;
  const start = skipLeadingNoise(source, rawStart, next, engineId);
  const end = trimTrailingWhitespace(source, start, next);
  return { start, end };
}

/**
 * Returns every top-level statement span in `source` as a half-open
 * `[start, end)` range, in source order. Statements are separated by `;`
 * (every dialect) and additionally by a line-leading `/` for Oracle. Spans
 * are NOT trimmed — callers that need leading-noise / trailing-whitespace
 * trimming should call `findEnclosingStatement` for the cursor's statement
 * or trim per slice. Used by the script-mode scope accumulator to fold
 * preceding statements into a `ScriptScope`.
 */
export function splitTopLevelStatements(
  source: string,
  engineId: DatabaseEngineId,
): readonly StatementSpan[] {
  const separators = scanTopLevelSeparators(source, engineId);
  const out: StatementSpan[] = [];
  let stmtStart = 0;
  for (const sep of separators) {
    out.push({ start: stmtStart, end: sep });
    stmtStart = sep + 1;
  }
  if (stmtStart < source.length) out.push({ start: stmtStart, end: source.length });
  return out;
}

function scanTopLevelSeparators(source: string, engineId: DatabaseEngineId): readonly number[] {
  const out: number[] = [];
  const length = source.length;
  let i = 0;
  while (i < length) {
    const c = source.charCodeAt(i);
    if (c === 0x2d /* - */ && source.charCodeAt(i + 1) === 0x2d) {
      i = skipToEol(source, i + 2);
      continue;
    }
    if (engineId === 'mysql' && c === 0x23 /* # */) {
      i = skipToEol(source, i + 1);
      continue;
    }
    if (c === 0x2f /* / */ && source.charCodeAt(i + 1) === 0x2a) {
      i = skipBlockComment(source, i + 2);
      continue;
    }
    if (c === 0x27 /* ' */) {
      i = skipSingleQuoted(source, i + 1);
      continue;
    }
    if (engineId === 'pgsql' && c === 0x24 /* $ */) {
      const end = tryDollarQuoted(source, i);
      if (end !== -1) {
        i = end;
        continue;
      }
    }
    if (c === 0x5b /* [ */ && (engineId === 'mssql' || engineId === 'sqlite')) {
      i = skipBracketedId(source, i + 1);
      continue;
    }
    if (engineId === 'mysql' && c === 0x60 /* ` */) {
      i = skipBacktickId(source, i + 1);
      continue;
    }
    if (c === 0x22 /* " */) {
      i = skipDoubleQuoted(source, i + 1);
      continue;
    }
    if (c === 0x3b /* ; */) {
      out.push(i);
      i += 1;
      continue;
    }
    if (engineId === 'oracle' && c === 0x2f /* / */ && isLineLeading(source, i)) {
      out.push(i);
      let j = i + 1;
      while (j < length && (source.charCodeAt(j) === 0x20 || source.charCodeAt(j) === 0x09)) j += 1;
      if (j < length && source.charCodeAt(j) === 0x0a) j += 1;
      else if (j < length && source.charCodeAt(j) === 0x0d) {
        j += 1;
        if (j < length && source.charCodeAt(j) === 0x0a) j += 1;
      }
      i = j;
      continue;
    }
    i += 1;
  }
  return out;
}

function skipLeadingNoise(
  source: string,
  from: number,
  limit: number,
  engineId: DatabaseEngineId,
): number {
  let i = from;
  while (i < limit) {
    const c = source.charCodeAt(i);
    if (c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d) {
      i += 1;
      continue;
    }
    if (c === 0x2d /* - */ && source.charCodeAt(i + 1) === 0x2d) {
      i = skipToEol(source, i + 2);
      continue;
    }
    if (engineId === 'mysql' && c === 0x23 /* # */) {
      i = skipToEol(source, i + 1);
      continue;
    }
    if (c === 0x2f /* / */ && source.charCodeAt(i + 1) === 0x2a) {
      i = skipBlockComment(source, i + 2);
      continue;
    }
    break;
  }
  return Math.min(i, limit);
}

function trimTrailingWhitespace(source: string, from: number, to: number): number {
  let i = to;
  while (i > from) {
    const c = source.charCodeAt(i - 1);
    if (c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d) {
      i -= 1;
      continue;
    }
    break;
  }
  return i;
}

function isLineLeading(source: string, at: number): boolean {
  let i = at - 1;
  while (i >= 0) {
    const c = source.charCodeAt(i);
    if (c === 0x0a || c === 0x0d) return true;
    if (c !== 0x20 && c !== 0x09) return false;
    i -= 1;
  }
  return true;
}
